import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import VoteEmojiSettings from '../components/VoteEmojiSettings';
import ContentManager from '../components/admin/ContentManager';
import ApprovalManager from '../components/admin/ApprovalManager';
import {
  Settings,
  Shield,
  Globe,
  Lock,
  Users,
  Eye,
  EyeOff,
  MessageSquare,
  Dog,
  Briefcase,
  Trees,
  Calendar,
  Building2,
  Heart,
  ShoppingBag,
  Loader2,
  Save,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  ThumbsUp,
  FileText,
  ShieldCheck,
  ToggleLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  forums: MessageSquare,
  breeds: Dog,
  services: Briefcase,
  dog_parks: Trees,
  events: Calendar,
  organizations: Building2,
  health_info: Heart,
  marketplace: ShoppingBag,
  groups: Users,
  vendors: Briefcase
};

const AdminSettings = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Feature toggles state
  const [clubToggles, setClubToggles] = useState([]);
  const [featureDefinitions, setFeatureDefinitions] = useState({});
  const [togglesLoading, setTogglesLoading] = useState(false);
  const [savingToggles, setSavingToggles] = useState(null);
  const [pendingToggleChanges, setPendingToggleChanges] = useState({});

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, configRes] = await Promise.all([
        axios.get(`${API}/admin/public-access/categories`),
        axios.get(`${API}/admin/public-access/config`)
      ]);
      setCategories(categoriesRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureToggles = async () => {
    setTogglesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [defsRes, clubsRes] = await Promise.all([
        axios.get(`${API}/feature-toggles/definitions`),
        axios.get(`${API}/feature-toggles/all-clubs`, { headers })
      ]);
      setFeatureDefinitions(defsRes.data.features);
      setClubToggles(clubsRes.data.clubs);
      setPendingToggleChanges({});
    } catch (error) {
      console.error('Error fetching feature toggles:', error);
    } finally {
      setTogglesLoading(false);
    }
  };

  const handleToggleChange = (clubSlug, featureKey, value) => {
    setPendingToggleChanges(prev => ({
      ...prev,
      [clubSlug]: {
        ...(prev[clubSlug] || {}),
        [featureKey]: value
      }
    }));
    // Update local state immediately for responsive UI
    setClubToggles(prev => prev.map(club => {
      if (club.slug === clubSlug) {
        return { ...club, features: { ...club.features, [featureKey]: value } };
      }
      return club;
    }));
  };

  const saveToggles = async (clubSlug) => {
    const changes = pendingToggleChanges[clubSlug];
    if (!changes || Object.keys(changes).length === 0) return;

    setSavingToggles(clubSlug);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/feature-toggles/club/${clubSlug}`,
        { features: changes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Feature toggles saved');
      setPendingToggleChanges(prev => { const n = { ...prev }; delete n[clubSlug]; return n; });
    } catch (error) {
      toast.error('Failed to save toggles');
    } finally {
      setSavingToggles(null);
    }
  };

  const handleCategoryToggle = (categoryId, isPublic) => {
    setHasChanges(true);
    const newPublicCategories = isPublic
      ? [...config.public_categories, categoryId]
      : config.public_categories.filter(id => id !== categoryId);
    
    setConfig({
      ...config,
      public_categories: newPublicCategories
    });
    
    // Update local categories state
    setCategories(categories.map(cat => 
      cat.id === categoryId ? { ...cat, is_public: isPublic } : cat
    ));
  };

  const handleConfigToggle = (field, value) => {
    setHasChanges(true);
    setConfig({
      ...config,
      [field]: value
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/public-access/config`, {
        public_categories: config.public_categories,
        allow_guest_forum_read: config.allow_guest_forum_read,
        allow_guest_breed_search: config.allow_guest_breed_search,
        allow_guest_service_search: config.allow_guest_service_search,
        allow_guest_park_search: config.allow_guest_park_search,
        allow_guest_event_view: config.allow_guest_event_view,
        require_login_for_details: config.require_login_for_details,
        show_login_prompt: config.show_login_prompt
      });
      toast.success('Settings saved successfully!');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all public access settings to defaults?')) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/public-access/reset`);
      toast.success('Settings reset to defaults');
      fetchData();
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="text-center py-16">
          <CardContent>
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h3 className="font-heading text-xl mb-2">Access Denied</h3>
            <p className="text-muted-foreground">You don't have permission to access admin settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Settings className="w-8 h-8 text-primary" />
              Admin Settings
            </h1>
            <p className="text-muted-foreground">
              Configure public access and site settings
            </p>
          </div>
        </div>

        <Tabs defaultValue="access" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:grid-cols-none lg:flex" data-testid="admin-settings-tabs">
            <TabsTrigger value="access" className="gap-2" data-testid="tab-public-access">
              <Globe className="w-4 h-4" />
              Public Access
            </TabsTrigger>
            <TabsTrigger value="features" className="gap-2" data-testid="tab-features" onClick={fetchFeatureToggles}>
              <ToggleLeft className="w-4 h-4" />
              Features
            </TabsTrigger>
            <TabsTrigger value="approvals" className="gap-2" data-testid="tab-approvals">
              <ShieldCheck className="w-4 h-4" />
              Approvals
            </TabsTrigger>
            <TabsTrigger value="content" className="gap-2" data-testid="tab-content">
              <FileText className="w-4 h-4" />
              Content
            </TabsTrigger>
            <TabsTrigger value="voting" className="gap-2" data-testid="tab-vote-display">
              <ThumbsUp className="w-4 h-4" />
              Vote Display
            </TabsTrigger>
          </TabsList>

          {/* Public Access Tab */}
          <TabsContent value="access" className="space-y-6">
            <div className="flex justify-end gap-2 mb-4">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={saving}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="btn-primary"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>

            {hasChanges && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <p className="text-amber-800">You have unsaved changes</p>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-8">
          {/* Public Categories */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Public Access Categories
              </CardTitle>
              <CardDescription>
                Choose which sections are accessible to unregistered guests (read-only)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {categories.map((category) => {
                  const Icon = categoryIcons[category.id] || Globe;
                  return (
                    <div
                      key={category.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        category.is_public ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          category.is_public ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            category.is_public ? 'text-green-600' : 'text-gray-400'
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{category.name}</h4>
                            {category.default_public && (
                              <Badge variant="outline" className="text-xs">Default</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{category.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {category.is_public ? (
                          <Badge className="bg-green-100 text-green-700">
                            <Globe className="w-3 h-3 mr-1" />
                            Public
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">
                            <Lock className="w-3 h-3 mr-1" />
                            Members
                          </Badge>
                        )}
                        <Switch
                          checked={category.is_public}
                          onCheckedChange={(checked) => handleCategoryToggle(category.id, checked)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Guest Access Settings */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Guest Viewing Options
                </CardTitle>
                <CardDescription>
                  Fine-tune what unregistered guests can see
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Forum Read Access</h4>
                    <p className="text-sm text-muted-foreground">Allow guests to read forum posts</p>
                  </div>
                  <Switch
                    checked={config.allow_guest_forum_read}
                    onCheckedChange={(val) => handleConfigToggle('allow_guest_forum_read', val)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Breed Search</h4>
                    <p className="text-sm text-muted-foreground">Allow guests to search breed database</p>
                  </div>
                  <Switch
                    checked={config.allow_guest_breed_search}
                    onCheckedChange={(val) => handleConfigToggle('allow_guest_breed_search', val)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Service Directory Search</h4>
                    <p className="text-sm text-muted-foreground">Allow guests to search for services</p>
                  </div>
                  <Switch
                    checked={config.allow_guest_service_search}
                    onCheckedChange={(val) => handleConfigToggle('allow_guest_service_search', val)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Dog Park Search</h4>
                    <p className="text-sm text-muted-foreground">Allow guests to find dog parks</p>
                  </div>
                  <Switch
                    checked={config.allow_guest_park_search}
                    onCheckedChange={(val) => handleConfigToggle('allow_guest_park_search', val)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Event Calendar</h4>
                    <p className="text-sm text-muted-foreground">Allow guests to view events</p>
                  </div>
                  <Switch
                    checked={config.allow_guest_event_view}
                    onCheckedChange={(val) => handleConfigToggle('allow_guest_event_view', val)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Access Restrictions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Require Login for Details</h4>
                    <p className="text-sm text-muted-foreground">Guests can see listings but must login for full details</p>
                  </div>
                  <Switch
                    checked={config.require_login_for_details}
                    onCheckedChange={(val) => handleConfigToggle('require_login_for_details', val)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Show Login Prompts</h4>
                    <p className="text-sm text-muted-foreground">Display "Login to interact" prompts for guests</p>
                  </div>
                  <Switch
                    checked={config.show_login_prompt}
                    onCheckedChange={(val) => handleConfigToggle('show_login_prompt', val)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-medium">Current Access Summary</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      <strong>{config.public_categories.length}</strong> categories are publicly accessible. 
                      Guests can browse {config.public_categories.length > 0 ? 
                        categories.filter(c => c.is_public).map(c => c.name.toLowerCase()).join(', ') : 
                        'no sections'
                      }.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
            </div>
          </TabsContent>

          {/* Content Manager Tab */}
          <TabsContent value="content" className="space-y-6" data-testid="content-tab-content">
            <ContentManager />
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals" className="space-y-6" data-testid="approvals-tab-content">
            <ApprovalManager />
          </TabsContent>

          {/* Vote Display Tab */}
          <TabsContent value="voting" className="space-y-6" data-testid="vote-display-tab-content">
            <VoteEmojiSettings 
              clubId="default" 
              onSave={() => toast.success('Vote display settings updated!')} 
            />
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <ThumbsUp className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-medium">About Vote Display</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Customize how votes appear on adoption agencies, user submissions, and other content with ELO ratings. 
                      Choose from preset emoji styles or create your own.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Toggles Tab */}
          <TabsContent value="features" className="space-y-6" data-testid="features-tab-content">
            {togglesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : clubToggles.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No clubs found. Click the Features tab to load data.
                </CardContent>
              </Card>
            ) : (
              clubToggles.map((club) => {
                const hasPending = pendingToggleChanges[club.slug] && Object.keys(pendingToggleChanges[club.slug]).length > 0;
                const categories = {};
                Object.entries(featureDefinitions).forEach(([key, def]) => {
                  const cat = def.category || 'other';
                  if (!categories[cat]) categories[cat] = [];
                  categories[cat].push({ key, ...def, enabled: club.features[key] ?? def.default });
                });

                return (
                  <Card key={club.slug} data-testid={`club-toggles-${club.slug}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{club.name}</CardTitle>
                          <CardDescription>
                            {Object.values(club.features).filter(Boolean).length}/{Object.keys(club.features).length} features enabled
                          </CardDescription>
                        </div>
                        {hasPending && (
                          <Button size="sm" onClick={() => saveToggles(club.slug)} disabled={savingToggles === club.slug}
                            data-testid={`save-toggles-${club.slug}`}>
                            {savingToggles === club.slug ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                            Save Changes
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {Object.entries(categories).map(([catName, features]) => (
                          <div key={catName}>
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 capitalize">{catName}</h4>
                            <div className="grid gap-3 sm:grid-cols-2">
                              {features.map((feature) => (
                                <div key={feature.key}
                                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                                  data-testid={`toggle-${club.slug}-${feature.key}`}>
                                  <div className="flex-1 min-w-0 mr-3">
                                    <p className="text-sm font-medium">{feature.label}</p>
                                    <p className="text-xs text-muted-foreground truncate">{feature.description}</p>
                                  </div>
                                  <Switch
                                    checked={feature.enabled}
                                    onCheckedChange={(val) => handleToggleChange(club.slug, feature.key, val)}
                                    data-testid={`switch-${club.slug}-${feature.key}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminSettings;
